import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { pivotAFOCLData } from '../hooks/useTablaValorAFOCL';
import { OperacionAFOCLTablaValorHistoricoGrupo } from '../types/dto';
import { OperacionAFOCLTablaValorHistoricoFields } from '../types/fields';
import { AFOCLTablaValores } from './SeccionAFOCLTablaValores';
import { Icon } from '@iconify/react';
import { useAFOCLHistorico } from '../hooks/useAFOCLHistorico';

interface AFOCLTablaValoresHistorialDialogProps {
    idOperacion: number;
    open: boolean;
    onClose: () => void;

}

export const AFOCLTablaValoresHistorialDialog = ({ idOperacion, open, onClose }: AFOCLTablaValoresHistorialDialogProps) => {
    const { versiones, isLoading } = useAFOCLHistorico(idOperacion);
    const [expandedVersion, setExpandedVersion] = useState<string | false>(false);
    
    useEffect(() => {
        if (versiones.length > 0 && !expandedVersion) {
            setExpandedVersion(versiones[0].fechaMod.toISOString());
        }
    }, [versiones]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
            <DialogTitle sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', mb: 4 }}>
                Historial de Cambios - Tabla de Valores
            </DialogTitle>
            <DialogContent>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
                        <CircularProgress />
                    </Box>
                ) : versiones.length === 0 ? (
                    <Typography sx={{ p: 4, textAlign: 'center' }}>
                        No se registran cambios históricos para esta operación.
                    </Typography>
                ) : (
                    <Box sx={{ mt: 2 }}>
                        {versiones.map((v) => {
                            const id = v.fechaMod.toISOString();

                            return (
                                <VersionHistoricaItem
                                    key={id}
                                    version={v}
                                    expanded={expandedVersion === id}
                                    onChange={(isExpanded) => setExpandedVersion(isExpanded ? id : false)}
                                />
                            );
                        })}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant='outlined'>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
};




interface VersionHistoricaItemProps {
    version: OperacionAFOCLTablaValorHistoricoGrupo;
    expanded: boolean;
    onChange: (isExpanded: boolean) => void;
}

const VersionHistoricaItem = ({ version, expanded, onChange }: VersionHistoricaItemProps) => {
    const { headers, rows } = useMemo(() => {
        const celdas = version[OperacionAFOCLTablaValorHistoricoFields.Celdas] || [];

        return pivotAFOCLData(celdas);
    }, [version]);

    const fecha = version[OperacionAFOCLTablaValorHistoricoFields.FechaModificacion];
    const usuario = version[OperacionAFOCLTablaValorHistoricoFields.NombreUsuarioModificacion];
    const observaciones = version[OperacionAFOCLTablaValorHistoricoFields.Observaciones];

    return (
        <Accordion expanded={expanded} onChange={(_, isExpanded) => onChange(isExpanded)}>
            <AccordionSummary expandIcon={<Icon icon='mdi:chevron-down' />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 4, alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 'bold' }}>
                            {fecha.toLocaleString()}
                        </Typography>
                        <Chip label={usuario} size='small' variant='outlined' color='primary' />
                    </Box>
                    {observaciones && (
                        <Typography variant='caption' sx={{ fontStyle: 'italic', color: 'text.secondary', maxWidth: '40%' }} noWrap>
                            {observaciones}
                        </Typography>
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                {observaciones && (
                    <Box sx={{ mb: 4, p: 3, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant='subtitle2'>Observaciones:</Typography>
                        <Typography variant='body2'>{observaciones}</Typography>
                    </Box>
                )}
                <AFOCLTablaValores headers={headers} rows={rows} />
            </AccordionDetails>
        </Accordion>
    );
};
